import { groups, sum } from '@visx/vendor/d3-array';
import { difference, first, flow, get, groupBy, last, orderBy, pick, startCase, uniq, uniqBy } from 'lodash';

import {
    ExecutionIntervalMeta,
    getExecutionColumnKeysByType,
    GetExecutionResponse,
    INTERVAL_BOUNDS,
} from '@blockworks/platform/api/research/studio/execution';
import {
    GroupConfig,
    SeriesConfig,
    StudioVisualizationType,
} from '@blockworks/platform/api/research/studio/visualizations';
import { utc, UTCDate } from '@blockworks/platform/libs/date-fns';

import { type GetVisualizationPropsResponse } from '../../../visualization/models';
import { interpolatePalette } from '../../components/charts/utils';
import { aggregators } from '../../utils/aggregators';
import { endOfInterval, getIntervalSize, maxRowsPerInterval } from '../../utils/end-of-interval';
import { initialTransforms, resolveComputedSeries } from '../utils';

const syntheticColumns = (rows: GetExecutionResponse['data']['rows'], groupBy: string) => {
    const uniqueValues = uniqBy(rows, groupBy).map(row => row[groupBy]);
    return uniqueValues;
};

const populateSyntheticSeries = ({
    columns,
    palleteOffset = columns.length,
    initialSettings = { type: 'line', summary: 'sum', axis: 'left', format: 'number', palette: 'brand' },
}: {
    columns: string[];
    palleteOffset?: number;
    initialSettings: any;
}) => {
    return columns.map((group, idx) => ({
        ...initialSettings,
        column: group,
        label: startCase(group),
        color: interpolatePalette(
            idx / palleteOffset,
            initialSettings.palette as Parameters<typeof interpolatePalette>[1],
        ),
    }));
};

const createSyntheticGroupData = (
    rows: GetExecutionResponse['data']['rows'],
    xColumn: string,
    groupCol: string,
    byCol: string,
) => {
    const byXValue = groupBy(rows, xColumn);
    const syntheticSeries = syntheticColumns(rows, groupCol);
    return Object.entries(byXValue).map(([xValue, datums]) => {
        const datum: Record<string, string | number> = { [xColumn]: xValue };
        syntheticSeries.forEach(group => {
            datum[group] = 0;
        });
        datums.forEach(groupDatum => {
            const group = groupDatum[groupCol];
            datum[group] = groupDatum[byCol];
        });
        return datum;
    });
};

const mergeUngroupedData = (rows: any[], numericCols: string[], xColumn: string) => {
    const uniqXValues = uniqBy(rows, row => row[xColumn]);
    const count = uniqXValues.length;
    const rowsPerGroup = rows.length / count;

    if (rowsPerGroup === 1) {
        return rows;
    }

    const byXValue = groupBy(rows, xColumn);

    const mergedRows = Object.values(byXValue).flatMap((chunk: any[]) => {
        return chunk.reduce((acc, next) => {
            const nextRow = { ...acc };
            numericCols.forEach(col => {
                if (col !== xColumn) {
                    const currentValue = nextRow[col];
                    if (currentValue) {
                        nextRow[col] = currentValue + next[col];
                    } else {
                        nextRow[col] = next[col];
                    }
                }
            });
            return nextRow;
        });
    });
    return mergedRows;
};

const validateConfig = (props: GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>) => {
    const { options, meta, params = {}, activeControls, data, ...rest } = props;

    if (options.xAxis.column === undefined) {
        throw new Error('xAxis column is not defined');
    }

    if (meta?.columnTypes[options.xAxis.column as keyof typeof meta] === undefined) {
        throw new Error(`xAxis.column ${options.xAxis.column} could not be found in column manifest`);
    }

    if (options.xAxis.orderBy && meta?.columnTypes[options.xAxis.orderBy as keyof typeof meta] === undefined) {
        throw new Error(`xAxis.orderBy ${options.xAxis.orderBy} could not be found in column manifest`);
    }

    return {
        ...rest,
        data,
        options,
        meta,
        params,
        activeControls,
    };
};

const applyParameterGroupParameter = (
    props: Required<GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>>,
) => {
    const { options, meta, params = {}, activeControls, ...rest } = props;

    if (params.groups) {
        options.groups = options.groups.filter(group => {
            return group.name === params.groups;
        });
    }

    options.groups = options.groups.flatMap(group => {
        const subgroups = groupBy(group.series, (series: any) => {
            return `${series.type}-${series.axis}-${series.format}`;
        });
        return Object.values(subgroups).map(baseSeries => {
            const firstSeries = baseSeries![0]! as any;
            return {
                ...group,
                axis: {
                    ...group.axis,
                    orientation: firstSeries.axis ?? group.axis.orientation,
                    format: firstSeries.format ?? group.axis.format,
                },
                type: firstSeries.type ?? group.type,
                series: baseSeries,
            };
        });
    });

    return {
        ...rest,
        options,
        meta,
        params,
        activeControls,
    };
};

const applyGrouping = (props: Required<GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>>) => {
    const { data: prevData = [], options: prevOptions, meta, params = {}, ...rest } = props;
    const xColumn = prevOptions.xAxis.column as string;
    const numericCols = getExecutionColumnKeysByType(meta.columnTypes, 'number');

    let data = [...prevData];
    let options = { ...prevOptions };
    if (prevOptions.xAxis.groupBy) {
        options = {
            ...prevOptions,
            groups: prevOptions.groups.map((group: any) => {
                const syntheticSeries = syntheticColumns(data, prevOptions.xAxis.groupBy!);
                const resolvedSeries = group.series.filter((series: SeriesConfig<any>) =>
                    syntheticSeries.includes(series.column),
                );

                const additionalGroups = difference(
                    syntheticSeries,
                    uniq(resolvedSeries.map((series: any) => series.column)),
                );

                if (additionalGroups.length === 0) {
                    return {
                        ...group,
                        series: resolvedSeries,
                    };
                }

                const additionalSeries = populateSyntheticSeries({
                    columns: additionalGroups,
                    palleteOffset: resolvedSeries.length,
                    initialSettings: {
                        type: group.type ?? 'line',
                        summary: group.summary ?? 'sum',
                        axis: group.axis.orientation ?? 'left',
                        format: group.axis.format ?? 'number',
                        palette: group.palette ?? 'brand',
                    },
                });

                return {
                    ...group,
                    series: [...resolvedSeries, ...additionalSeries].sort((_, b) => {
                        /** TODO: Not this */
                        if (b.column.toLowerCase() === 'other') return -1;
                        return 0;
                    }),
                };
            }),
        };

        data = createSyntheticGroupData(
            data,
            xColumn,
            options.xAxis.groupBy as string,
            options.groups[0]?.synthetic as string,
        );
    } else {
        data = mergeUngroupedData(data, numericCols, xColumn);
    }

    return {
        ...rest,
        data,
        options,
        meta,
        params,
    };
};

const applyIntervalParameter = (props: Required<GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>>) => {
    const { data: prevData = [], options, meta, params = {}, ...rest } = props;
    let data = [...prevData];

    const parameterTimeframe = params.timeframe;
    const xColumn = options.xAxis.column as string;
    options.xAxis.type = 'date';

    if (!meta.dimensions[xColumn]) {
        options.xAxis.type = 'enum';
        if (parseInt(data[0][xColumn], 10).toString() === data[0][xColumn]) {
            data = data.map(row => ({ ...row, [xColumn]: Number(row[xColumn]) }));
        }

        return {
            ...props,
            data,
        };
    }

    const { unit: initialTimeframe } = meta.dimensions[xColumn] as ExecutionIntervalMeta;

    const activeTimeframe = parameterTimeframe ?? initialTimeframe;
    options.xAxis.format = get(INTERVAL_BOUNDS, activeTimeframe).format;

    const computedSeries = options.groups
        .flatMap(group => group.series.filter(series => series.computed))
        .map(({ column, summary = 'sum', expression }) => {
            return {
                col: column,
                agg: (data: any[]) => {
                    const resolved = resolveComputedSeries(expression!, aggregators[summary], data);
                    return Math.abs(resolved) === Infinity ? undefined : resolved;
                },
            };
        });

    const standardSeries = options.groups
        .flatMap(group => group.series.filter(series => !series.computed))
        .map(({ column, summary = 'sum' }) => {
            return {
                col: column,
                agg: aggregators[summary],
            };
        });

    const getEndOfInterval = endOfInterval[params.timeframe as keyof typeof endOfInterval];
    const shouldAggregateData = activeTimeframe && activeTimeframe !== initialTimeframe;

    if (getEndOfInterval && shouldAggregateData) {
        const intervalChunks = groups(data, d => getEndOfInterval(new UTCDate(d[xColumn]), { in: utc }));

        const intervalGroups = intervalChunks.filter(([date, chunk]) => {
            const checkRequiredChunkLength = getIntervalSize[params.timeframe as keyof typeof getIntervalSize];
            const getExpectedChunksize = (checkRequiredChunkLength?.[
                initialTimeframe as keyof typeof checkRequiredChunkLength
            ] ?? (() => 1)) as (date: Date) => number;

            return chunk.length >= getExpectedChunksize(date);
        });

        // Apply summary to each data interval
        data = intervalGroups.map(([_, chunk]) => {
            const orderedChunk = orderBy(chunk, xColumn, 'asc');

            const firstDatum = first(orderedChunk);
            const lastDatum = last(orderedChunk);
            const next = { ...firstDatum, [xColumn]: lastDatum[xColumn] };

            // Standard Series

            standardSeries.forEach(({ col, agg }) => {
                const availabeChunks = orderedChunk.filter((row: any) => row[col] !== null);
                if (availabeChunks.length !== 0) {
                    next[col] = agg(availabeChunks, (row: any) => row[col]);
                } else {
                    next[col] = null;
                }
            });

            // Computed series

            computedSeries.forEach(({ col, agg }) => {
                if (orderedChunk.length !== 0) {
                    next[col] = agg(orderedChunk);
                } else {
                    next[col] = null;
                }
            });

            // Use the first row of the chunk as the date
            return next;
        });
    } else if (computedSeries.length > 0) {
        data = data.map(row => {
            const next = { ...row };
            computedSeries.forEach(({ col, agg }) => {
                if (row[col] !== null && agg) {
                    next[col] = agg([row]);
                } else {
                    next[col] = null;
                }
            });
            return next;
        });
    }

    let maxRows = maxRowsPerInterval[activeTimeframe as keyof typeof maxRowsPerInterval];
    if (options.brush) {
        maxRows = Infinity;
    }

    return {
        ...rest,
        params,
        meta,
        options: options,
        data: orderBy(data, xColumn, 'desc').slice(0, maxRows),
    } satisfies GetVisualizationPropsResponse<any>;
};

const applyTransformParameter = (
    props: Required<GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>>,
) => {
    if (!props.params.transform) return props;

    const { data: prevData = [], options, meta, params = {}, ...rest } = props;
    let data = [...prevData];
    const isPercentShare = params.transform === 'percent';
    options.groups = options.groups.map(group => {
        if (['bar', 'area'].includes(group.type)) {
            const format = isPercentShare ? 'percent' : group.axis.format;

            return {
                ...group,
                stack: true,
                axis: {
                    ...group.axis,
                    format: format,
                },
            };
        }
        return group;
    });

    const transformsToApply = options.groups
        .filter((group: GroupConfig<any>) => group.stack)
        .map(({ series }: GroupConfig<any>) => {
            return { transform: params.transform, cols: series.map(({ column }) => column) };
        });

    if (transformsToApply.length > 0) {
        const descCumSum = new Map();
        for (const group of transformsToApply) {
            const { transform, cols } = group;
            if (transform === 'cumulative') {
                cols.forEach((col: string) => {
                    descCumSum.set(
                        col,
                        sum(prevData, row => row[col]),
                    );
                });
            }
        }

        data = prevData.map((row: any) => {
            const nextRow = { ...row };
            for (const group of transformsToApply) {
                const { transform, cols } = group;
                if (transform === 'percent') {
                    const rowBasis = sum(Object.values(pick(nextRow, cols)));
                    cols.forEach((col: string) => {
                        nextRow[col] = nextRow[col] / rowBasis;
                    });
                    return nextRow;
                }
                if (transform === 'cumulative') {
                    cols.forEach((col: string) => {
                        const current = descCumSum.get(col) ?? 0;
                        nextRow[col] = current - nextRow[col];
                        descCumSum.set(col, nextRow[col]);
                    });
                }
            }

            return nextRow;
        });
    }

    return {
        ...rest,
        data,
        options,
        meta,
        params,
    };
};

const timeseriesWorkflow = flow(
    initialTransforms,
    validateConfig,
    applyParameterGroupParameter,
    applyGrouping,
    applyIntervalParameter,
    applyTransformParameter,
);

export { timeseriesWorkflow };
